import { FunctionComponent, useEffect, useState } from "react";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";
import { EmploymentType } from "@/enum/job.enum";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";

import { HiringConfig } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";
import { SuggestingTagField } from "./FilterFields";

const LOCATION_DEBOUNCE_MS = 400;

const EMPLOYMENT_TYPES: { value: EmploymentType; labelKey: string }[] = [
	{ value: EmploymentType.FULLTIME, labelKey: "employmentFulltime" },
	{ value: EmploymentType.PARTTIME, labelKey: "employmentParttime" },
	{ value: EmploymentType.CONTRACTOR, labelKey: "employmentContractor" },
	{ value: EmploymentType.INTERN, labelKey: "employmentIntern" },
	{ value: EmploymentType.TEMPORARY, labelKey: "employmentTemporary" },
];

/** JOBS — the role a target company is hiring for, where, and who to reach there. */
export const HiringForm: FunctionComponent<ConfigFormProps<HiringConfig>> = ({
	value,
	errors,
	options,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	const [fetchSuggestions] = useLazyGeSuggestionQuery();
	const [searchValue, setSearchValue] = useState("");
	const [locationOptions, setLocationOptions] = useState<AutoCompleteItem[]>([]);

	useEffect(() => {
		if (searchValue.trim().length < 2) {
			return;
		}

		const handle = setTimeout(async () => {
			try {
				const results = await fetchSuggestions({
					type: "location",
					keyword: searchValue.trim(),
				}).unwrap();

				setLocationOptions(
					results.suggestions.map((entry) => ({ id: entry.urn, title: entry.title }))
				);
			} catch (err) {
				console.error(err);
			}
		}, LOCATION_DEBOUNCE_MS);

		return () => clearTimeout(handle);
	}, [searchValue, fetchSuggestions]);

	return (
		<ConfigGrid>
			<ConfigField required error={errorFor("hiringFor")} label={ts("hiringFor")}>
				<InputField
					error={Boolean(errors.hiringFor)}
					handleChange={(event) => onChange({ ...value, query: event.target.value })}
					name="hiringQuery"
					placeholder={ts("hiringForPlaceholder")}
					showError={Boolean(errors.hiringFor)}
					value={value.query}
				/>
			</ConfigField>

			<ConfigField required error={errorFor("location")} label={ts("location")}>
				<AutoComplete
					data={locationOptions}
					error={Boolean(errors.location)}
					handleSearch={setSearchValue}
					handleSelect={(item) =>
						onChange({
							...value,
							location: item ? { id: String(item.id), name: item.title } : null,
						})
					}
					placeholder={ts("locationPlaceholder")}
					selectedItem={value.location?.name ?? ""}
				/>
			</ConfigField>

			<ConfigField label={ts("employmentType")}>
				<SelectField
					hideEmptyValue
					handleSelect={(employmentType) =>
						onChange({ ...value, employmentType: String(employmentType) })
					}
					options={EMPLOYMENT_TYPES.map(({ value: type, labelKey }) => ({
						title: ts(labelKey),
						value: type,
					}))}
					selectedOption={value.employmentType}
					testId="onboarding-hiring-employmentType"
				/>
			</ConfigField>

			<SuggestingTagField
				fetchFn={options.fetchJobTitles}
				label={ts("findTitles")}
				placeholder={ts("findTitlesPlaceholder")}
				testId="onboarding-hiring-positionTitles"
				values={value.positionTitles}
				onChange={(positionTitles) => onChange({ ...value, positionTitles })}
			/>
		</ConfigGrid>
	);
};
